import { useLayoutEffect, type MouseEvent } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Page transitions with the browser's View Transitions API (1.2.6). React Router's `viewTransition` option only
 * works with a data router, and PRISM uses <BrowserRouter>, whose navigations render in a React transition, so
 * a navigation is wrapped here instead: the browser keeps the old page as a picture until the new location has
 * been committed (`ViewTransitionSettle`, at most 400 ms), then animates between them (CSS: styles.css, "motion").
 * Where the API is missing the navigation simply happens, as before.
 */
let settle: (() => void) | null = null;

type StartViewTransition = (update: () => Promise<void>) => unknown;

export function withViewTransition(go: () => void) {
  const start = (document as Document & { startViewTransition?: StartViewTransition }).startViewTransition;
  if (typeof start !== 'function') { go(); return; }
  start.call(document, () => new Promise<void>((resolve) => {
    const done = () => { clearTimeout(timer); if (settle === done) settle = null; resolve(); };
    const timer = setTimeout(done, 400);
    settle?.();
    settle = done;
    go();
  }));
}

/** A plain left click (no new tab or window wanted), which a link may turn into a page transition. */
export function plainClick(e: MouseEvent) {
  return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.defaultPrevented;
}

/** Rendered once inside the router: tells a running page transition that the new location is on screen. */
export function ViewTransitionSettle() {
  const { key } = useLocation();
  useLayoutEffect(() => { settle?.(); }, [key]);
  return null;
}
